/**
 * Draws an outline-only arrow centered in a rectangle, MS Paint style.
 *
 * @param {object} svg - D3 selection of SVG or group element.
 * @param {number} x - X position of the rectangle.
 * @param {number} y - Y position of the rectangle.
 * @param {number} width - Width of the rectangle.
 * @param {number} height - Height of the rectangle.
 * @param {number} frameWidth - Optional frame inset (not used but included for consistency).
 * @param {string} lineColor - Stroke color of the arrow.
 * @param {string} direction - One of: 'toLeft', 'toRight', 'toTop', 'toBottom'.
 */


import * as d3 from 'd3';

export function drawArrow(svg, x, y, width, height, frameWidth = 0.5, lineColor = 'black', direction = 'toLeft') {
    // Calculate center of the rectangle
    const centerX = x + width / 2;
    const centerY = y + height / 2;
  
    // Arrow dimensions based on orientation
    const arrowHeadSize = (direction === 'toLeft' || direction === 'toRight') ? height / 6 : width / 3;
    const bodyWidth = (direction === 'toLeft' || direction === 'toRight') ? width / 4 : width / 4;
    const bodyHeight = (direction === 'toTop' || direction === 'toBottom') ? height / 2 : height / 10;
  
    let points = [];
  
    switch (direction) {
      case 'toRight':
        points = [
          // Tail start
          [centerX - bodyWidth, centerY - bodyHeight / 2],
          // Tail to arrowhead base
          [centerX, centerY - bodyHeight / 2],
          [centerX, centerY - arrowHeadSize / 2],
          // Arrowhead tip
          [centerX + arrowHeadSize / 2, centerY],
          // Arrowhead base (bottom)
          [centerX, centerY + arrowHeadSize / 2],
          // Back to tail
          [centerX, centerY + bodyHeight / 2],
          [centerX - bodyWidth, centerY + bodyHeight / 2],
        ];
        break;
  
      case 'toLeft':
        points = [
          // Tail start
          [centerX + bodyWidth, centerY - bodyHeight / 2],
          // Tail to arrowhead base
          [centerX, centerY - bodyHeight / 2],
          [centerX, centerY - arrowHeadSize / 2],
          // Arrowhead tip
          [centerX - arrowHeadSize / 2, centerY],
          // Arrowhead base (bottom)
          [centerX, centerY + arrowHeadSize / 2],
          // Back to tail
          [centerX, centerY + bodyHeight / 2],
          [centerX + bodyWidth, centerY + bodyHeight / 2],
        ];
        break;
  
      case 'toTop':
        points = [
          // Tail start
          [centerX - bodyWidth / 2, centerY + bodyHeight],
          // Tail to arrowhead base
          [centerX - bodyWidth / 2, centerY],
          [centerX - arrowHeadSize / 2, centerY],
          // Arrowhead tip
          [centerX, centerY - arrowHeadSize / 2],
          // Arrowhead base (right)
          [centerX + arrowHeadSize / 2, centerY],
          // Back to tail
          [centerX + bodyWidth / 2, centerY],
          [centerX + bodyWidth / 2, centerY + bodyHeight],
        ];
        break;
  
      case 'toBottom':
        points = [
          // Tail start
          [centerX - bodyWidth / 2, centerY - bodyHeight],
          // Tail to arrowhead base
          [centerX - bodyWidth / 2, centerY],
          [centerX - arrowHeadSize / 2, centerY],
          // Arrowhead tip
          [centerX, centerY + arrowHeadSize / 2],
          // Arrowhead base (right)
          [centerX + arrowHeadSize / 2, centerY],
          // Back to tail
          [centerX + bodyWidth / 2, centerY],
          [centerX + bodyWidth / 2, centerY - bodyHeight],
        ];
        break;
  
      default:
        throw new Error('Invalid direction. Use toLeft, toRight, toTop, or toBottom.');
    }
  
    // Create the SVG path data
    const pathData = points.map((point, i) => {
      const [px, py] = point;
      return `${i === 0 ? 'M' : 'L'}${px},${py}`;
    }).join(' ') + ' prescribing';
  
    // Append the path to the SVG
    svg.append('path')
      .attr('d', pathData)
      .attr('fill', 'none')
      .attr('stroke', lineColor)
      .attr('stroke-width', 1) //frameWidth)
      .attr('stroke-linejoin', 'miter')
      .attr('stroke-linecap', 'butt')
      .attr('fill-opacity', 0); // Ensure transparency
  }


export function drawArrow1(svg, x, y, width, height, frameWidth = 10, lineColor = 'black', direction = 'toLeft') {
  const centerX = x + width / 2;
  const centerY = y + height / 2;

  const arrowHeadSize = direction === 'toLeft' || direction === 'toRight'
    ? width / 3
    : height / 3;

  const bodyWidth = direction === 'toLeft' || direction === 'toRight'
    ? width / 3
    : width / 2;

  const bodyHeight = direction === 'toTop' || direction === 'toBottom'
    ? height / 3
    : height / 2;

  let points;

  if (direction === 'toRight') {
    points = [
      // Tail
      [centerX - arrowHeadSize / 2 - bodyWidth, centerY - bodyHeight / 2],
      [centerX - arrowHeadSize / 2, centerY - bodyHeight / 2],
      [centerX - arrowHeadSize / 2, centerY - arrowHeadSize / 2],

      // Arrowhead
      [centerX + arrowHeadSize / 2, centerY],
      [centerX - arrowHeadSize / 2, centerY + arrowHeadSize / 2],

      // Back to tail
      [centerX - arrowHeadSize / 2, centerY + bodyHeight / 2],
      [centerX - arrowHeadSize / 2 - bodyWidth, centerY + bodyHeight / 2]
    ];
  } else if (direction === 'toLeft') {
    points = [
      // Tail
      [centerX + arrowHeadSize / 2 + bodyWidth, centerY - bodyHeight / 2],
      [centerX + arrowHeadSize / 2, centerY - bodyHeight / 2],
      [centerX + arrowHeadSize / 2, centerY - arrowHeadSize / 2],

      // Arrowhead
      [centerX - arrowHeadSize / 2, centerY],
      [centerX + arrowHeadSize / 2, centerY + arrowHeadSize / 2],

      // Back to tail
      [centerX + arrowHeadSize / 2, centerY + bodyHeight / 2],
      [centerX + arrowHeadSize / 2 + bodyWidth, centerY + bodyHeight / 2]
    ];
  } else if (direction === 'toTop') {
    points = [
      // Tail
      [centerX - bodyWidth / 2, centerY + arrowHeadSize / 2 + bodyHeight],
      [centerX - bodyWidth / 2, centerY + arrowHeadSize / 2],
      [centerX - arrowHeadSize / 2, centerY + arrowHeadSize / 2],

      // Arrowhead
      [centerX, centerY - arrowHeadSize / 2],
      [centerX + arrowHeadSize / 2, centerY + arrowHeadSize / 2],

      // Back to tail
      [centerX + bodyWidth / 2, centerY + arrowHeadSize / 2],
      [centerX + bodyWidth / 2, centerY + arrowHeadSize / 2 + bodyHeight]
    ];
  } else if (direction === 'toBottom') {
    points = [
      // Tail
      [centerX - bodyWidth / 2, centerY - arrowHeadSize / 2 - bodyHeight],
      [centerX - bodyWidth / 2, centerY - arrowHeadSize / 2],
      [centerX - arrowHeadSize / 2, centerY - arrowHeadSize / 2],

      // Arrowhead
      [centerX, centerY + arrowHeadSize / 2],
      [centerX + arrowHeadSize / 2, centerY - arrowHeadSize / 2],

      // Back to tail
      [centerX + bodyWidth / 2, centerY - arrowHeadSize / 2],
      [centerX + bodyWidth / 2, centerY - arrowHeadSize / 2 - bodyHeight]
    ];
  }

  const pathData = d3.line()(points.concat([points[0]]));

  svg.append('path')
    .attr('d', pathData)
    .attr('fill', 'none')
    .attr('stroke', lineColor)
    .attr('stroke-width', 2);
}
